import moment from 'moment';
import {
  ActionTypes,
  daysAbbreviation,
  ProgramTypes,
  RECURRENCE_PATTERN,
} from '../ChannelArchitecture.d';
import findRelatedBlocks from '../helper/findRelatedBlocks';
import { reconstructBlockName, toPascalCase } from './architecture.utils';

/**
 * Builds a daily recurrence pattern for events.
 *
 * @param {number} day - The day of the week (1 = Monday, 2 = Tuesday, ..., 7 = Sunday).
 * @param {number} weeks - The recurrence week (1 = Week 1, 2 = Week 2, ..., 0 = Every Week).
 * @param {Object} block - The event block that will be generated based on the recurrence pattern.
 * @param {number} intervalId - The ID of the interval for which the blocks will be created.
 * 1: Every {Monday | Tuesday | Wednesday | Thursday | Friday | Saturday | Sunday}
 * 2: Every Weekday
 * 3: Every Weekend
 */
export const buildDailyRecurrence = (day, weeks, block, intervalId) => {
  const generatedSchedules = [];
  const linkReferenceName = (dayNo, startTime) =>
    `${dayNo}${moment(startTime, 'HH:mm').format('HH:mm')}`;
  let daysToAdd = [];
  let prevBlock = block.blockReference ? block.blockReference : '';

  switch (intervalId) {
    case 1: //every selected day
      daysToAdd = [day];
      break;
    case 2: //every weekdays
      daysToAdd = [1, 2, 3, 4, 5];
      break;
    case 3: //every weekends
      daysToAdd = [6, 7];
      break;
    default:
      break;
  }

  if (!weeks) {
    // Block/s will be generated every week for {numberOfWeeks} weeks
    [...Array(numberOfWeeks).keys()].forEach((weekIteration) => {
      daysToAdd.forEach((value) => {
        const linkReference = linkReferenceName(daysAbbreviation[value - 1], block.startTime);
        generatedSchedules.push({
          ...block,
          id: crypto.randomUUID(),
          week: weekIteration + 1,
          dayOfWeek: value,
          blockName: linkReference,
          link: prevBlock != '' ? true : block.link,
          blockReference: prevBlock,
        });
        prevBlock = linkReference;
      });
    });
  } else {
    // Block/s will be generated for week {weeks}
    daysToAdd.forEach((value) => {
      const linkReference = linkReferenceName(daysAbbreviation[value - 1], block.startTime);
      generatedSchedules.push({
        ...block,
        id: crypto.randomUUID(),
        week: weeks,
        dayOfWeek: value,
        blockName: linkReference,
        link: prevBlock != '' ? true : block.link,
        blockReference: prevBlock,
      });
      prevBlock = linkReference;
    });
  }
  return generatedSchedules;
};

const getBlockName = (day, startTime) =>
  daysAbbreviation[day - 1] + moment(startTime, ['HH:mm:ss', 'HH:mm']).format('HH:mm');

const getDurationInMinutes = (startTime, endTime) => {
  const start = moment(startTime, ['HH:mm:ss', 'HH:mm'], true);
  const end = moment(endTime, ['HH:mm:ss', 'HH:mm'], true);

  if (!start.isValid() || !end.isValid()) return 0;

  const duration = end.diff(start, 'minutes');
  return duration > 0 ? duration : duration + 24 * 60;
};

const getMinutesUntilDayEnd = (startTime, isBroadcast) => {
  const start = moment(startTime, ['HH:mm:ss', 'HH:mm'], true);
  if (!start.isValid()) return 0;

  const dayEndMinutes = isBroadcast ? 6 * 60 : 24 * 60;
  const startMinutes = start.hours() * 60 + start.minutes();
  const minutesUntilEnd = dayEndMinutes - startMinutes;

  return minutesUntilEnd > 0 ? minutesUntilEnd : minutesUntilEnd + 24 * 60;
};

/**
 * Links the first generated block to the last generated block in the generated recurrence pattern.
 * This ensures that the recurrence pattern forms a closed loop, allowing for continuous
 * playback of the blocks.
 *
 * @param {Array} generatedPattern - The array of generated blocks to be linked.
 * @returns {Array} - The modified array of generated blocks with the first block
 * linked to the last block.
 */
function closeGeneratedPattern(generatedPattern) {
  if (generatedPattern.length <= 1) return generatedPattern;

  const [firstBlock, ...restBlocks] = generatedPattern;
  const lastBlock = restBlocks.at(-1);

  // A block with a blockReference means its manually linked to another block, so
  // we don't want to override that and break the link.
  if (firstBlock.blockReference) return generatedPattern;

  return [
    { ...firstBlock, link: true, blockReference: lastBlock.blockName },
    ...restBlocks,
  ];
}

function hasMatchingBlockName(schedule, blockReference) {
  return reconstructBlockName(schedule.blockName) === reconstructBlockName(blockReference);
}

function findLinkedProgram(schedules, blockReference) {
  const programmes = schedules?.programmes ?? [];
  return programmes.find((schedule) => hasMatchingBlockName(schedule, blockReference));
}

const getLinkedBlocks = ({ block, blockData, modalState, modalType, schedules }) => {
  if (modalType !== ActionTypes.REPEAT && modalState.type !== ProgramTypes.REPEAT) {
    return [block];
  }

  const linkedProgram = findLinkedProgram(schedules, block.blockReference);

  let selectedBlock = blockData || linkedProgram;
  if (modalType === ActionTypes.REPEAT || modalType === ActionTypes.CREATE) {
    selectedBlock = linkedProgram;
  }
  if (!selectedBlock) return linkedProgram ? [linkedProgram] : [block];

  const allLinkedBlocks = findRelatedBlocks(toPascalCase(selectedBlock), schedules.programmes, {
    includeRepeats: false,
    includeSelected: true,
    requireExactName: true,
    searchEntireSchedule: true,
  });

  if (allLinkedBlocks.length) return allLinkedBlocks;
  return linkedProgram ? [linkedProgram] : [block];
};

const buildStripRecurrence = ({ block, blockData, days, modalState, modalType, schedules }) => {
  const toInsert = [];
  const isModalSingleRepeatType = modalType === ActionTypes.REPEAT;
  const isModalCreateRepeatType =
    modalType === ActionTypes.CREATE && modalState.type === ProgramTypes.REPEAT;
  const isModalMultipleRepeatType = modalType === ActionTypes.MULTIPLEREPEAT;
  let prevBlock = block.blockReference ? block.blockReference : '';

  if (modalType !== ActionTypes.REPEAT && modalState.type !== ProgramTypes.REPEAT) {
    days.forEach((value) => {
      const blockName = getBlockName(value, block.startTime);
      toInsert.push({
        ...block,
        id: crypto.randomUUID(),
        week: 1,
        dayOfWeek: value,
        blockName,
        link: prevBlock.length !== 0 ? true : block.link,
        blockReference: prevBlock,
      });
      prevBlock = blockName;
    });
  } else {
    prevBlock = block.blockReference;
    const linkedProgram = findLinkedProgram(schedules, block.blockReference);
    const linkedStartTime = linkedProgram?.startTime;

    if (isModalSingleRepeatType || isModalCreateRepeatType) {
      days.forEach((value) => {
        const blockReference = isModalSingleRepeatType ? block.blockReference : prevBlock;
        toInsert.push({
          ...block,
          id: crypto.randomUUID(),
          week: 1,
          dayOfWeek: value,
          blockName: getBlockName(value, block.startTime),
          link: blockReference !== '' ? true : block.link,
          blockReference,
        });
        if (isModalCreateRepeatType && linkedStartTime) {
          prevBlock = getBlockName(value, linkedStartTime);
        }
      });
    }

    if (isModalMultipleRepeatType) {
      const selectedBlock = modalType === ActionTypes.CREATE ? linkedProgram : blockData;
      let allLinkedBlocks = [];
      if (selectedBlock) {
        allLinkedBlocks = findRelatedBlocks(toPascalCase(selectedBlock), schedules.programmes, {
          includeRepeats: false,
          includeSelected: true,
          requireExactName: true,
          searchEntireSchedule: false,
        });
      }

      days.forEach((value, index) => {
        if (allLinkedBlocks[index]) {
          toInsert.push({
            ...block,
            id: crypto.randomUUID(),
            week: 1,
            dayOfWeek: value,
            blockName: getBlockName(value, block.startTime),
            link: prevBlock !== '' ? true : block.link,
            blockReference: allLinkedBlocks[index].blockName,
          });
        }
      });
    }
  }

  return closeGeneratedPattern(toInsert);
};

const buildStackRecurrence = ({
  block,
  blockData,
  days,
  isBroadcast,
  modalState,
  modalType,
  schedules,
}) => {
  // Resolve the source chain and ignore invalid or zero-length templates.
  const templates = getLinkedBlocks({
    block,
    blockData,
    modalState,
    modalType,
    schedules,
  }).filter((template) => getDurationInMinutes(template.startTime, template.endTime) > 0);
  const targetDuration = getDurationInMinutes(block.startTime, block.endTime);

  if (!templates.length || !targetDuration) return [];

  const generatedSchedules = [];
  const isSingleRepeat = modalType === ActionTypes.REPEAT;
  const isRepeat = modalType === ActionTypes.REPEAT || modalState.type === ProgramTypes.REPEAT;

  days.forEach((day, dayIndex) => {
    const dayStart = moment(block.startTime, ['HH:mm:ss', 'HH:mm'], true);
    const minutesUntilDayEnd = getMinutesUntilDayEnd(block.startTime, isBroadcast);
    if (!dayStart.isValid() || !minutesUntilDayEnd) return;

    if (isRepeat) {
      // Distribute the linked templates as evenly as possible across the selected days.
      const slotsPerDay = Math.floor(templates.length / days.length);
      const extraSlots = templates.length % days.length;
      const firstTemplateIndex = dayIndex * slotsPerDay + Math.min(dayIndex, extraSlots);
      const slotsForDay = slotsPerDay + (dayIndex < extraSlots ? 1 : 0);
      const templatesForDay = templates.slice(firstTemplateIndex, firstTemplateIndex + slotsForDay);
      let elapsedMinutes = 0;

      templatesForDay.forEach((template) => {
        if (elapsedMinutes >= minutesUntilDayEnd) return;

        const generatedDuration = Math.min(targetDuration, minutesUntilDayEnd - elapsedMinutes);
        const startTime = dayStart.clone().add(elapsedMinutes, 'minutes');
        const endTime = startTime.clone().add(generatedDuration, 'minutes');
        const blockName = getBlockName(day, startTime);
        const isFirstSlot = elapsedMinutes === 0;

        generatedSchedules.push({
          ...block,
          id: crypto.randomUUID(),
          week: 1,
          dayOfWeek: day,
          startTime: isFirstSlot ? block.startTime : startTime.format('HH:mm:ss'),
          endTime: isFirstSlot ? block.endTime : endTime.format('HH:mm:ss'),
          blockName,
          link: true,
          blockReference: isSingleRepeat ? block.blockReference : template.blockName,
        });

        elapsedMinutes += generatedDuration;
      });

      return;
    }

    let elapsedMinutes = 0;
    let previousBlock = block.blockReference || '';

    // Cycle the submitted template sequence until the target day boundary.
    while (elapsedMinutes < minutesUntilDayEnd) {
      const generatedDuration = Math.min(targetDuration, minutesUntilDayEnd - elapsedMinutes);
      const startTime = dayStart.clone().add(elapsedMinutes, 'minutes');
      const endTime = startTime.clone().add(generatedDuration, 'minutes');
      const blockName = getBlockName(day, startTime);
      const blockReference = previousBlock;
      const isFirstSlot = elapsedMinutes === 0;

      generatedSchedules.push({
        ...block,
        id: crypto.randomUUID(),
        week: 1,
        dayOfWeek: day,
        startTime: isFirstSlot ? block.startTime : startTime.format('HH:mm:ss'),
        endTime: isFirstSlot ? block.endTime : endTime.format('HH:mm:ss'),
        blockName,
        link: blockReference ? true : block.link,
        blockReference,
      });

      previousBlock = blockName;
      elapsedMinutes += generatedDuration;
    }
  });

  return closeGeneratedPattern(generatedSchedules);
};

export const buildWeeklyRecurrence = ({
  block,
  blockData,
  isBroadcast = false,
  modal: { state: modalState, type: modalType },
  recurrence: { days, pattern = RECURRENCE_PATTERN.STRIP },
  schedules,
}) => {
  const recurrenceArgs = {
    block,
    blockData,
    days,
    modalState,
    modalType,
    schedules,
  };

  if (pattern === RECURRENCE_PATTERN.STACK) {
    return buildStackRecurrence({ ...recurrenceArgs, isBroadcast });
  }

  return buildStripRecurrence(recurrenceArgs);
};
